import { comuneLabel } from '@sse/gazetteer';
import {
  CATEGORIES,
  RASSEGNA_THEMES,
  editionRange,
  periodProblem,
  type Category,
  type Rassegna,
  type RassegnaEdition,
  type RassegnaMember,
} from '@sse/schema';
import { ArrowLeft, Bot, Plus, RefreshCw, Search, Trash2, Undo2, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import {
  Card,
  Field,
  StatusBadge,
  btnGhost,
  btnPrimary,
  btnSecondary,
  inputCls,
} from '../components/ui';
import { ApiError, type AdminEventRow } from '../lib/api';
import { categoryLabel, formatRange, formatStamp } from '../lib/format';
import { useSession } from '../lib/session';
import { EditionBadge } from './rassegne';

interface Member extends RassegnaMember {
  status: string;
  startDate?: string;
  endDate?: string;
  comune?: string;
}

interface Detail {
  rassegna: Rassegna;
  year: number;
  range: { start: string; end: string };
  editions: RassegnaEdition[];
  members: Member[];
}

type Form = Pick<
  Rassegna,
  | 'slug'
  | 'title'
  | 'intro'
  | 'description'
  | 'criteria'
  | 'from'
  | 'to'
  | 'notifyDays'
  | 'categories'
  | 'enabled'
  | 'theme'
>;

const empty: Form = {
  slug: '',
  title: '',
  intro: '',
  description: '',
  criteria: '',
  from: '',
  to: '',
  notifyDays: 10,
  categories: [],
  enabled: true,
};

const slugify = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);

const THEME_LABEL: Record<(typeof RASSEGNA_THEMES)[number], string> = {
  luminarie: 'Luminarie (feste d’estate)',
  autunno: 'Autunno (foglie)',
  natale: 'Natale (stelle)',
};

const BOUND = /^((0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])|E[+-]\d{1,3})$/;

/**
 * Scheda di una rassegna: testi e periodo, criteri per l'AI, edizioni (attiva, salta, ripeti la
 * ricerca) ed eventi dell'anno, con aggiunta e rimozione a mano.
 */
export function RassegnaEditor() {
  const { slug } = useParams();
  const isNew = !slug;
  const [sp, setSp] = useSearchParams();
  const { api, isAdmin } = useSession();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<Detail | null>(null);
  const [form, setForm] = useState<Form>(empty);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const yearParam = sp.get('year');

  const load = async () => {
    if (!slug) return;
    try {
      const d = await api<Detail>(`/rassegne/${slug}${yearParam ? `?year=${yearParam}` : ''}`);
      setDetail(d);
      setForm(d.rassegna);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };
  useEffect(() => {
    if (isNew) {
      setDetail(null);
      setForm(empty);
    } else void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, yearParam]);

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));
  const periodOk = BOUND.test(form.from) && BOUND.test(form.to);
  const problem = periodOk ? periodProblem(form) : null;
  const thisYear = new Date().getFullYear();
  const preview = periodOk && !problem ? editionRange(form, thisYear) : null;

  const save = async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      // nessuna decorazione: il campo non si manda
      const { theme, ...rest } = form;
      const body = theme ? { ...rest, theme } : rest;
      if (isNew) {
        await api('/rassegne', { method: 'POST', body });
        navigate(`/rassegne/${form.slug}`, { replace: true });
      } else {
        const r = await api<{ rassegna: Rassegna }>(`/rassegne/${slug}`, {
          method: 'PUT',
          body: { ...body, version: detail!.rassegna.version },
        });
        setDetail((d) => (d ? { ...d, rassegna: r.rassegna } : d));
        setNotice('Rassegna salvata.');
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!detail) return;
    if (
      !window.confirm(
        `Eliminare la rassegna «${detail.rassegna.title}» con tutte le sue edizioni? Gli eventi restano.`,
      )
    )
      return;
    await api(`/rassegne/${slug}`, { method: 'DELETE' });
    navigate('/rassegne', { replace: true });
  };

  return (
    <main className="mx-auto max-w-[1400px] p-4 pb-24 md:p-6">
      <Link to="/rassegne" className={btnGhost}>
        <ArrowLeft className="size-4" aria-hidden /> Rassegne
      </Link>
      <h1 className="mt-2 font-display text-h2">
        {isNew ? 'Nuova rassegna' : (detail?.rassegna.title ?? '…')}
      </h1>
      {error && (
        <p role="alert" className="mt-4 bg-danger-soft p-3 text-sm">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="mt-4 bg-surface p-3 text-sm">
          {notice}
        </p>
      )}

      <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        <div className="flex flex-col gap-4">
          <Card title="Rassegna">
            <Field label="Titolo">
              <input
                className={inputCls}
                value={form.title}
                maxLength={80}
                onChange={(e) => {
                  set('title', e.target.value);
                  if (isNew) set('slug', slugify(e.target.value));
                }}
              />
            </Field>
            <Field
              label="Indirizzo sul sito"
              hint={`/collections/${form.slug || '…'}${isNew ? '' : ' (non si cambia)'}`}
            >
              <input
                className={inputCls}
                value={form.slug}
                disabled={!isNew}
                maxLength={40}
                onChange={(e) => set('slug', slugify(e.target.value))}
              />
            </Field>
            <Field label="Sottotitolo" hint="Una frase, nell’elenco e in cima alla pagina.">
              <input
                className={inputCls}
                value={form.intro}
                maxLength={300}
                onChange={(e) => set('intro', e.target.value)}
              />
            </Field>
            <Field label="Testo della pagina" hint="Markdown, facoltativo.">
              <textarea
                className={`${inputCls} h-28 py-2`}
                value={form.description}
                maxLength={4000}
                onChange={(e) => set('description', e.target.value)}
              />
            </Field>
            <Field
              label="Criteri per l’AI"
              hint="Quali eventi ne fanno parte: tipi di evento, parole chiave, cosa escludere. Non si pubblica."
            >
              <textarea
                className={`${inputCls} h-28 py-2`}
                value={form.criteria}
                maxLength={2000}
                onChange={(e) => set('criteria', e.target.value)}
              />
            </Field>
          </Card>

          <Card title="Periodo dell’anno">
            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Dal"
                error={form.from && !BOUND.test(form.from) ? 'MM-GG o E-9' : undefined}
              >
                <input
                  className={inputCls}
                  value={form.from}
                  placeholder="12-08"
                  onChange={(e) => set('from', e.target.value.trim())}
                />
              </Field>
              <Field label="Al" error={form.to && !BOUND.test(form.to) ? 'MM-GG o E+1' : undefined}>
                <input
                  className={inputCls}
                  value={form.to}
                  placeholder="01-06"
                  onChange={(e) => set('to', e.target.value.trim())}
                />
              </Field>
            </div>
            <p className="text-xs text-muted">
              Mese-giorno (12-08 = 8 dicembre; se la fine viene prima dell’inizio il periodo passa
              l’anno) oppure giorni da Pasqua (E-9, E+1) per le feste mobili.
            </p>
            {problem && <p className="text-xs font-medium text-danger">{problem}</p>}
            {preview && (
              <p className="text-sm">
                Edizione {thisYear}: {formatRange(preview.start, preview.end)}
              </p>
            )}
            <Field
              label="Proposta sul bot"
              hint="Giorni prima dell’inizio in cui il bot chiede se attivarla."
            >
              <input
                type="number"
                min={0}
                max={60}
                className={`${inputCls} w-28`}
                value={form.notifyDays}
                onChange={(e) => set('notifyDays', Number(e.target.value))}
              />
            </Field>
            <fieldset>
              <legend className="mb-1 text-label font-medium uppercase tracking-[0.04em] text-muted">
                Solo queste categorie
              </legend>
              <p className="mb-2 text-xs text-muted">
                Nessuna scelta: l’AI valuta gli eventi di tutte le categorie (costa di più).
              </p>
              <div className="flex flex-wrap gap-1">
                {CATEGORIES.map((c) => {
                  const on = form.categories.includes(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      aria-pressed={on}
                      onClick={() =>
                        set(
                          'categories',
                          on
                            ? form.categories.filter((x) => x !== c)
                            : [...form.categories, c as Category],
                        )
                      }
                      className={`h-8 px-2 text-xs ${on ? 'bg-fg text-page' : 'bg-surface hover:bg-surface-strong'}`}
                    >
                      {categoryLabel(c)}
                    </button>
                  );
                })}
              </div>
            </fieldset>
            <Field
              label="Decorazione del sito"
              hint="Durante l’edizione in corso, una fascia decorata sotto l’intestazione di tutte le pagine."
            >
              <select
                className={inputCls}
                value={form.theme ?? ''}
                onChange={(e) =>
                  setForm((f) => {
                    const { theme: _old, ...rest } = f;
                    void _old;
                    const v = e.target.value as (typeof RASSEGNA_THEMES)[number] | '';
                    return v ? { ...rest, theme: v } : rest;
                  })
                }
              >
                <option value="">Nessuna</option>
                {RASSEGNA_THEMES.map((th) => (
                  <option key={th} value={th}>
                    {THEME_LABEL[th]}
                  </option>
                ))}
              </select>
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => set('enabled', e.target.checked)}
              />
              Attiva: il bot la propone ogni anno
            </label>
          </Card>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => void save()}
              disabled={saving || !form.title || !form.slug || !periodOk || Boolean(problem)}
              className={btnPrimary}
            >
              {saving ? 'Salvataggio…' : isNew ? 'Crea rassegna' : 'Salva'}
            </button>
            {!isNew && isAdmin && (
              <button
                onClick={() => void remove()}
                className={`${btnSecondary} border-danger text-danger`}
              >
                <Trash2 className="size-4" aria-hidden /> Elimina
              </button>
            )}
          </div>
        </div>

        {detail && (
          <EditionPanel
            detail={detail}
            onYear={(y) => setSp({ year: String(y) }, { replace: true })}
            reload={load}
          />
        )}
      </div>
    </main>
  );
}

function EditionPanel({
  detail,
  onYear,
  reload,
}: {
  detail: Detail;
  onYear: (y: number) => void;
  reload: () => Promise<void>;
}) {
  const { api, config } = useSession();
  const { rassegna, year, range } = detail;
  const ed = detail.editions.find((e) => e.year === year);
  const status = ed?.status ?? 'none';
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const years = useMemo(
    () => [...new Set([year - 1, year, year + 1, ...detail.editions.map((e) => e.year)])].sort(),
    [detail.editions, year],
  );
  const shown = detail.members.filter((m) => !m.excluded);
  const excluded = detail.members.filter((m) => m.excluded);

  const act = async (action: 'activate' | 'skip' | 'populate') => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await api<{ populating: boolean }>(`/rassegne/${rassegna.slug}/editions/${year}`, {
        method: 'POST',
        body: { action },
      });
      setMsg(
        action === 'skip'
          ? 'Edizione saltata: non si vede sul sito.'
          : r.populating
            ? 'L’AI sta cercando gli eventi: ci vuole qualche minuto, poi arriva il riepilogo su Telegram. Aggiorna la pagina per vederli.'
            : 'Edizione attiva. La ricerca con l’AI non è disponibile in questo ambiente.',
      );
      await reload();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const exclude = async (m: Member) => {
    await api(`/rassegne/${rassegna.slug}/editions/${year}/members/${m.eventId}`, {
      method: 'DELETE',
    });
    await reload();
  };
  const add = async (eventId: string) => {
    await api(`/rassegne/${rassegna.slug}/editions/${year}/members`, {
      method: 'POST',
      body: { eventId },
    });
    await reload();
  };

  return (
    <div className="flex flex-col gap-4">
      <Card
        title="Edizione"
        actions={
          <select
            value={year}
            onChange={(e) => onYear(Number(e.target.value))}
            className="h-8 border border-fg/25 bg-page px-1 text-sm"
            aria-label="Anno"
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        }
      >
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <EditionBadge status={status} />
          <span>{formatRange(ed?.start ?? range.start, ed?.end ?? range.end)}</span>
          {status === 'active' && (
            <a
              href={`${config.siteUrl}/collections/${rassegna.slug}`}
              target="_blank"
              rel="noopener"
              className="underline"
            >
              pagina sul sito
            </a>
          )}
        </div>
        {ed?.populatedAt && (
          <p className="text-xs text-muted">
            Ultima ricerca con l’AI: {formatStamp(ed.populatedAt)}
            {ed.populate && ` · ${ed.populate.added} aggiunti su ${ed.populate.checked} valutati`}
            {ed.populate?.error && ` · ${ed.populate.error}`}
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          {status !== 'active' && (
            <button onClick={() => void act('activate')} disabled={busy} className={btnPrimary}>
              <Bot className="size-4" aria-hidden /> Attiva e cerca gli eventi
            </button>
          )}
          {status === 'active' && (
            <button onClick={() => void act('populate')} disabled={busy} className={btnSecondary}>
              <RefreshCw className="size-4" aria-hidden /> Ripeti la ricerca con l’AI
            </button>
          )}
          {status !== 'skipped' && (
            <button onClick={() => void act('skip')} disabled={busy} className={btnGhost}>
              <X className="size-4" aria-hidden />{' '}
              {status === 'active' ? 'Disattiva' : 'Salta quest’anno'}
            </button>
          )}
          <button onClick={() => void reload()} className={btnGhost}>
            <RefreshCw className="size-4" aria-hidden /> Aggiorna
          </button>
        </div>
        {msg && <p className="text-sm">{msg}</p>}
      </Card>

      <Card title={`Eventi ${year} (${shown.length})`}>
        {shown.length === 0 && (
          <p className="text-sm text-muted">
            Ancora nessun evento.{' '}
            {status === 'active' ? 'Aggiungili a mano o ripeti la ricerca.' : 'Attiva l’edizione.'}
          </p>
        )}
        {shown.length > 0 && (
          <ul className="flex flex-col divide-y divide-surface-strong">
            {shown.map((m) => (
              <li key={m.eventId} className="flex flex-wrap items-start gap-2 py-2 text-sm">
                <div className="min-w-0 flex-1">
                  <Link to={`/events/${m.eventId}`} className="font-medium hover:underline">
                    {m.title}
                  </Link>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
                    {m.startDate && (
                      <span>{formatRange(m.startDate, m.endDate ?? m.startDate)}</span>
                    )}
                    {m.comune && <span>{m.comune}</span>}
                    {m.status !== 'published' &&
                      (m.status === 'deleted' ? (
                        <span>eliminato</span>
                      ) : (
                        <StatusBadge status={m.status as 'draft'} />
                      ))}
                    <span>{m.addedBy === 'ai' ? `AI: ${m.reason ?? ''}` : 'aggiunto a mano'}</span>
                  </div>
                </div>
                <button
                  onClick={() => void exclude(m)}
                  className={btnGhost}
                  title="Togli dalla rassegna (l’AI non lo riproporrà)"
                >
                  <X className="size-4" aria-hidden /> Togli
                </button>
              </li>
            ))}
          </ul>
        )}
        {excluded.length > 0 && (
          <details className="text-sm">
            <summary className="cursor-pointer text-muted">
              Tolti dalla redazione ({excluded.length})
            </summary>
            <ul className="mt-2 flex flex-col gap-1">
              {excluded.map((m) => (
                <li key={m.eventId} className="flex items-center gap-2">
                  <span className="text-muted">{m.title}</span>
                  <button onClick={() => void add(m.eventId)} className={btnGhost}>
                    <Undo2 className="size-4" aria-hidden /> Rimetti
                  </button>
                </li>
              ))}
            </ul>
          </details>
        )}
        {ed && (
          <AddEvent
            range={{ start: ed.start, end: ed.end }}
            exclude={new Set(detail.members.map((m) => m.eventId))}
            onAdd={add}
          />
        )}
      </Card>
    </div>
  );
}

/** Ricerca di un evento del periodo da aggiungere a mano. */
function AddEvent({
  range,
  exclude,
  onAdd,
}: {
  range: { start: string; end: string };
  exclude: Set<string>;
  onAdd: (id: string) => Promise<void>;
}) {
  const { api } = useSession();
  const [text, setText] = useState('');
  const [rows, setRows] = useState<AdminEventRow[] | null>(null);
  const q = text.trim();
  useEffect(() => {
    if (q.length < 3) {
      setRows(null);
      return;
    }
    const t = setTimeout(() => {
      const p = new URLSearchParams({
        status: 'published',
        from: range.start,
        to: range.end,
        q,
        limit: '20',
      });
      void api<{ items: AdminEventRow[] }>(`/events?${p}`).then((r) => setRows(r.items));
    }, 400);
    return () => clearTimeout(t);
  }, [api, q, range.start, range.end]);

  return (
    <div className="border-t border-surface-strong pt-3">
      <label className="relative block">
        <span className="sr-only">Aggiungi un evento</span>
        <Search className="absolute top-3 left-2 size-4 text-muted" aria-hidden />
        <input
          type="search"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Aggiungi un evento del periodo: cerca per titolo o comune…"
          className={`${inputCls} pl-8`}
        />
      </label>
      {rows && rows.filter((r) => !exclude.has(r.id)).length === 0 && (
        <p className="mt-2 text-sm text-muted">Nessun evento pubblicato nel periodo.</p>
      )}
      {rows && (
        <ul className="mt-2 flex flex-col">
          {rows
            .filter((r) => !exclude.has(r.id))
            .map((r) => (
              <li key={r.id}>
                <button
                  onClick={() => void onAdd(r.id).then(() => setText(''))}
                  className="flex w-full flex-wrap items-center gap-2 px-2 py-1.5 text-left text-sm hover:bg-surface"
                >
                  <Plus className="size-4" aria-hidden />
                  <span className="font-medium">{r.title}</span>
                  <span className="text-xs text-muted">
                    {formatRange(r.startDate, r.endDate)} · {comuneLabel(r.comuneId) || r.comune}
                  </span>
                </button>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}
